// The always-visible location directory — the map's twin.
//
// DIRECTORY (widget-directory-list): this list ALWAYS shows individual location
// cards (title + address + description + distance), even while the map clusters —
// the standard store-locator pattern. The server returns the same individual rows
// regardless of how the map draws them, so there is no "zoom in to see locations"
// dead end.
//
// SYNC (task-031 AC): hover/selection is bidirectional. Hovering a card
// emphasises its map marker and vice-versa; selecting a card centers the map on
// that pin. The server already sorts priority-first then distance (F-111), so we
// NEVER re-sort client-side, and infinite-scroll appends deeper pages in order.
//
// A11Y: rows use roving tabindex for arrow-key navigation, Enter/Space selects,
// and an aria-live count announces result changes to screen readers.

import type { ViewportFilters, WidgetLocationRow } from "../data/types";
import { startRetryCountdown } from "./states";
import { formatDistance, type ResolvedUnit } from "../util/units";

/** Callbacks into the component so it can mirror list interaction onto the map. */
export interface ListHandlers {
  onHover(id: string | null): void;
  onSelect(id: string): void;
  /** Near the bottom of the scroll with more pages available — fetch + append the next page. */
  onLoadMore(): void;
}

export class ListView {
  readonly element: HTMLElement;
  private readonly titleEl: HTMLElement;
  private readonly countEl: HTMLElement;
  private readonly scrollEl: HTMLElement;
  private readonly listEl: HTMLElement;
  private readonly disclosureEl: HTMLElement;
  private rows = new Map<string, HTMLElement>();
  /** Ordered ids for roving-tabindex keyboard navigation. */
  private order: string[] = [];
  /** Whether a deeper page exists (drives infinite scroll). */
  private hasMore = false;
  /** Guards against firing onLoadMore repeatedly while a page fetch is in flight. */
  private loadingMore = false;

  constructor(
    private readonly locatorName: string,
    private readonly unit: ResolvedUnit,
    private readonly languageTag: string | undefined,
    private readonly handlers: ListHandlers,
  ) {
    this.element = div("list-pane");

    const head = div("list-head");
    this.titleEl = tag("p", "list-title", this.locatorName);
    this.countEl = tag("p", "list-count", "");
    // Polite live region: announces "12 locations" as the viewport changes.
    this.countEl.setAttribute("aria-live", "polite");
    this.countEl.setAttribute("role", "status");
    head.append(this.titleEl, this.countEl);

    this.scrollEl = div("list-scroll");
    this.listEl = document.createElement("ul");
    this.listEl.className = "list";
    this.listEl.setAttribute("role", "listbox");
    this.listEl.setAttribute("aria-label", `${this.locatorName} locations`);
    this.scrollEl.appendChild(this.listEl);

    this.disclosureEl = div("disclosure");
    this.disclosureEl.hidden = true;

    this.element.append(head, this.scrollEl, this.disclosureEl);
    this.listEl.addEventListener("keydown", (e) => this.onKeyDown(e));
    // Infinite scroll (widget-directory-list): near the bottom with more pages,
    // ask the component to fetch + append the next page. Guarded so the same page
    // is never requested twice mid-flight.
    this.scrollEl.addEventListener("scroll", () => this.maybeLoadMore());
  }

  /** Fires onLoadMore once when the scroll nears the bottom and another page exists. */
  private maybeLoadMore(): void {
    if (!this.hasMore || this.loadingMore) {
      return;
    }
    const remaining = this.scrollEl.scrollHeight - this.scrollEl.scrollTop - this.scrollEl.clientHeight;
    if (remaining <= LOAD_MORE_THRESHOLD_PX) {
      this.loadingMore = true;
      this.handlers.onLoadMore();
    }
  }

  /**
   * Renders the first page of the directory (widget-directory-list): individual
   * location cards, always — regardless of whether the MAP is clustering. Order is
   * the server's; never re-sorted. Resets the scroll to the top (a new viewport).
   */
  renderLocations(
    locations: WidgetLocationRow[],
    filters: ViewportFilters,
    hasMore: boolean,
    scanCapReached: boolean,
  ): void {
    this.listEl.replaceChildren();
    this.rows.clear();
    this.order = [];
    this.scrollEl.scrollTop = 0;

    for (const loc of locations) {
      this.appendRow(loc);
    }
    // First row is the initial tab stop (roving tabindex).
    if (this.order.length > 0) {
      this.rows.get(this.order[0])!.tabIndex = 0;
    }

    this.hasMore = hasMore;
    this.loadingMore = false;
    this.countEl.textContent = countLabel(this.order.length, hasMore);
    this.renderDisclosure(filters, scanCapReached);
  }

  /**
   * Appends a deeper page (infinite scroll) without disturbing the rows already
   * shown or the visitor's scroll position. Order continues the server's sort.
   */
  appendLocations(locations: WidgetLocationRow[], hasMore: boolean): void {
    for (const loc of locations) {
      // Defensive against an overlapping fetch returning a row already shown.
      if (!this.rows.has(loc.id)) {
        this.appendRow(loc);
      }
    }
    this.hasMore = hasMore;
    this.loadingMore = false;
    this.countEl.textContent = countLabel(this.order.length, hasMore);
    // A short scroll may still sit within the threshold after appending; re-check
    // so a tall viewport keeps pulling pages until it is filled or exhausted.
    this.maybeLoadMore();
  }

  /** Builds a card and registers it in the row/order maps. */
  private appendRow(loc: WidgetLocationRow): void {
    const li = this.locationRow(loc);
    this.listEl.appendChild(li);
    this.rows.set(loc.id, li);
    this.order.push(loc.id);
  }

  /**
   * F-032 honest empty state: an active search + radius matched nothing. Distinct
   * from the "this locator has no locations" terminal state — offers a real
   * recovery action (widen radius) instead of a dead end.
   */
  renderNoResultsInRadius(radiusLabel: string, onWidenRadius: () => void): void {
    this.listEl.replaceChildren();
    this.rows.clear();
    this.order = [];
    this.hasMore = false;
    this.loadingMore = false;
    const li = document.createElement("li");
    li.className = "list-item list-item-message";
    const message = tag("span", "list-meta", `No locations within ${radiusLabel}.`);
    const button = document.createElement("button");
    button.type = "button";
    button.className = "list-widen-button";
    button.textContent = "Widen radius";
    button.addEventListener("click", onWidenRadius);
    li.append(message, button);
    this.listEl.appendChild(li);
    this.countEl.textContent = "No locations in this area";
    this.disclosureEl.hidden = true;
  }

  /**
   * A failed viewport fetch, shown INSIDE the directory rather than over the whole widget.
   *
   * The search bar, filters and map remain on screen and usable, and — the point — this list stays
   * attached to the document, so a successful retry renders its rows straight back in here. The
   * previous full-pane error detached the list, which made a successful retry paint into nothing.
   * Same shape as {@link renderNoResultsInRadius}: an honest sentence and one real action.
   */
  renderFetchError(message: string, onRetry: () => void, retryAfterSeconds: number | null): void {
    this.listEl.replaceChildren();
    this.rows.clear();
    this.order = [];
    this.hasMore = false;
    this.loadingMore = false;

    const li = document.createElement("li");
    li.className = "list-item list-item-message";
    li.setAttribute("role", "alert");
    const button = document.createElement("button");
    button.type = "button";
    button.className = "list-widen-button";
    button.textContent = "Try again";
    button.addEventListener("click", onRetry);
    li.append(tag("span", "list-meta", message), button);
    this.listEl.appendChild(li);

    if (retryAfterSeconds != null && retryAfterSeconds > 0) {
      startRetryCountdown(button, retryAfterSeconds);
    }
    this.countEl.textContent = "Couldn't load locations";
    this.disclosureEl.hidden = true;
  }

  /** Map -> list highlight sync. */
  highlight(id: string | null): void {
    this.rows.forEach((row, rowId) => {
      row.classList.toggle("is-active", rowId === id);
    });
  }

  /** Map selection sync: highlight and scroll the row into view. */
  select(id: string): void {
    this.highlight(id);
    this.rows.get(id)?.scrollIntoView({ block: "nearest" });
  }

  /**
   * One directory card: title + (optional) address + (optional) description +
   * (optional) distance. Absent fields render nothing — never a placeholder or a
   * fabricated line (house rule: honest absence).
   */
  private locationRow(loc: WidgetLocationRow): HTMLElement {
    const li = document.createElement("li");
    li.className = "list-item";
    li.setAttribute("role", "option");
    li.tabIndex = -1;
    li.dataset.id = loc.id;
    li.appendChild(tag("span", "list-name", loc.name));

    if (loc.address != null && loc.address.trim().length > 0) {
      li.appendChild(tag("span", "list-address", loc.address));
    }
    if (loc.description != null && loc.description.trim().length > 0) {
      li.appendChild(tag("span", "list-description", loc.description));
    }

    const distance = formatDistance(loc.distanceMeters, this.unit, this.languageTag);
    if (distance != null) {
      li.appendChild(tag("span", "list-meta", `${distance} away`));
    }

    li.addEventListener("mouseenter", () => this.handlers.onHover(loc.id));
    li.addEventListener("mouseleave", () => this.handlers.onHover(null));
    li.addEventListener("focus", () => this.handlers.onHover(loc.id));
    li.addEventListener("click", () => this.handlers.onSelect(loc.id));
    return li;
  }

  /**
   * Honest disclosure of filter side effects (F-018): locations hidden because
   * their timezone is unknown, and whether the open-now scan hit its cap.
   */
  private renderDisclosure(filters: ViewportFilters, scanCapReached: boolean): void {
    const parts: string[] = [];
    if (filters.timezoneUnknownExcluded > 0) {
      parts.push(
        `${filters.timezoneUnknownExcluded} ${plural(filters.timezoneUnknownExcluded, "location", "locations")} hidden (opening hours unavailable).`,
      );
    }
    if (scanCapReached) {
      parts.push("More matches may exist than are shown — zoom in to narrow the area.");
    }
    if (parts.length === 0) {
      this.disclosureEl.hidden = true;
      this.disclosureEl.textContent = "";
    } else {
      this.disclosureEl.hidden = false;
      this.disclosureEl.textContent = parts.join(" ");
    }
  }

  /** Roving-tabindex arrow navigation over the rows. */
  private onKeyDown(event: KeyboardEvent): void {
    if (this.order.length === 0) {
      return;
    }
    const current = document.activeElement;
    const activeId = current instanceof HTMLElement ? current.dataset.id : undefined;
    let index = activeId ? this.order.indexOf(activeId) : -1;

    switch (event.key) {
      case "ArrowDown":
        index = Math.min(this.order.length - 1, index + 1);
        break;
      case "ArrowUp":
        index = Math.max(0, index - 1);
        break;
      case "Home":
        index = 0;
        break;
      case "End":
        index = this.order.length - 1;
        break;
      case "Enter":
      case " ":
        if (activeId) {
          event.preventDefault();
          this.handlers.onSelect(activeId);
        }
        return;
      default:
        return;
    }
    event.preventDefault();
    this.moveFocus(index);
  }

  private moveFocus(index: number): void {
    const id = this.order[index];
    const row = this.rows.get(id);
    if (!row) {
      return;
    }
    this.rows.forEach((r) => (r.tabIndex = -1));
    row.tabIndex = 0;
    row.focus();
  }
}

/** How close to the bottom of the scroll (px) triggers the next-page fetch. */
const LOAD_MORE_THRESHOLD_PX = 160;

function countLabel(count: number, hasMore: boolean): string {
  if (count === 0) {
    return "No locations in this area";
  }
  const noun = plural(count, "location", "locations");
  // The count grows as deeper pages append; "+" honestly signals more remain.
  return hasMore ? `${count}+ ${noun} — scroll for more` : `${count} ${noun}`;
}

function plural(n: number, one: string, many: string): string {
  return n === 1 ? one : many;
}

function div(className: string): HTMLElement {
  const node = document.createElement("div");
  node.className = className;
  return node;
}

function tag(name: string, className: string, content: string): HTMLElement {
  const node = document.createElement(name);
  node.className = className;
  node.textContent = content;
  return node;
}
